import cn from "classnames";
import React from "react";

import { IconSizes } from "@2u-landing-pages/types";
import { normalizeIconName } from "@2u-landing-pages/utils/icon-name-aliases";
// Lucide path data keyed by icon name. Rendered inline as SVG so icons are
// present in the server-rendered HTML on first paint — no webfont download, no
// FOIT, and no font-load race in SSG/SSR or visual-regression tests.
import iconNodes from "lucide-static/icon-nodes.json";
// @ts-ignore
import styles from "./Icon.module.scss";

// Each node is a tuple of [svgTag, attributes], e.g. ["circle", { cx: "12", ... }].
type IconNode = [string, Record<string, string | number>];

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: string;
  size?: IconSizes;
}

export const Icon: React.FC<IconProps> = ({ name, size = "md", className, style, ...rest }) => {
  const normalizedName = normalizeIconName(name);
  const nodes = (iconNodes as unknown as Record<string, IconNode[]>)[normalizedName];

  // Unknown icon name → render nothing (matches the old behaviour: no glyph).
  if (!nodes) return null;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn(`icon-${normalizedName}`, styles[size], className)}
      style={style}
      {...rest}
    >
      {nodes.map(([tag, attrs], index) =>
        React.createElement(tag, {
          key: index,
          fill: "none",
          stroke: "currentColor",
          ...attrs,
        })
      )}
    </svg>
  );
};
